import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, CalendarDays, MapPin, Users, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_OPTIONS = ["ALL", "DRAFT", "PUBLISHED", "EXPIRED"];

const statusStyle = (s) => {
  if (s === "PUBLISHED") return "text-lv-active bg-lv-active/10";
  if (s === "EXPIRED") return "text-lv-inactive bg-lv-inactive/10";
  return "text-amber-400 bg-amber-400/10";
};

export default function EventList() {
  const [statusFilter, setStatusFilter] = useState("ALL");

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["admin-events", statusFilter],
    queryFn: () => api.get(`/admin/events${statusFilter !== "ALL" ? `?status=${statusFilter}` : ""}`),
  });

  if (isLoading) return <div className="py-12 text-center"><div className="w-10 h-0.5 bg-bronze mx-auto animate-pulse" /></div>;

  return (
    <div data-testid="event-list-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide">Events</h1>
          <p className="text-sm text-lv-muted tracking-wide mt-1">{events.length} event{events.length !== 1 ? "s" : ""}</p>
        </div>
        <Link to="/admin/events/new" className="lv-btn-primary inline-flex items-center gap-2 min-h-[44px]" data-testid="create-event-btn">
          <Plus size={16} /> New Event
        </Link>
      </div>

      <div className="mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px] bg-walnut-deep border-lv-border text-cream min-h-[44px]" data-testid="event-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-walnut-deep border-lv-border">
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {events.map((ev) => (
          <Link
            key={ev.id}
            to={`/admin/events/${ev.id}`}
            className="block bg-walnut border border-lv-border p-4 sm:p-5 hover:border-bronze/40 transition-colors group"
            data-testid={`event-row-${ev.id}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <h3 className="text-sm text-cream font-medium tracking-wide">{ev.title}</h3>
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 ${statusStyle(ev.status)}`}>{ev.status}</span>
                  {ev.event_type && <span className="text-[9px] uppercase tracking-wider text-lv-muted/60">{ev.event_type}</span>}
                </div>
                <div className="flex items-center gap-4 text-xs text-lv-muted flex-wrap">
                  {ev.date && <span className="inline-flex items-center gap-1"><CalendarDays size={11} /> {ev.date}</span>}
                  {ev.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {ev.location}</span>}
                  {ev.audience && <span className="inline-flex items-center gap-1"><Users size={11} /> {ev.audience.replace(/_/g, " ")}</span>}
                  {ev.capacity && <span>Cap: {ev.capacity}</span>}
                </div>
              </div>
              <ChevronRight size={16} className="text-lv-muted group-hover:text-bronze transition-colors shrink-0" />
            </div>
          </Link>
        ))}
        {events.length === 0 && <p className="text-sm text-lv-muted text-center py-8">No events found.</p>}
      </div>
    </div>
  );
}
